/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import Anchor from "./Anchor";
import T from "./Translation";
import { useTranslate } from "../../hooks/useTranslate";

const StyledAnchor = styled((props) => <Anchor {...props} />)`
  ${(props) => sizeStyles[props.$size]}
`;

const StyledText = styled.p`
  ${(props) =>
    !!props.$lineClamp &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: ${props.$lineClamp};
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      max-height: calc(${props.$lineClamp} * 2em);
    `}
  ${(props) => sizeStyles[props.$size]}
    ${(props) => weightStyles[props.$weight]}
    ${(props) => colorStyles[props.$color]}
    ${(props) => fontStyles[props.$fontStyle]}
    word-break: break-word;

  ${(props) =>
    props.dangerouslySetInnerHTML &&
    css`
      & > * {
        all: revert !important;
      }
      & * {
        color: rgba(${({ theme }) => theme.colors.text.rgb}) !important;
      }
      iframe, img, svg {
        width: 100% !important;
        max-width: 100%;
        display: block;
      }
      iframe + br, img + br, svg + br {
        display: none;
      }
      a {
        color: rgba(${({ theme }) => theme.colors.link.rgb}) !important;
        text-decoration: underline !important;
        @media (hover: hover) {
          &:hover {
            opacity: 0.8;
          }
        }
      }
    `}
`;

const Text = ({
  children,
  size = "default",
  weight = "regular",
  color = "default",
  fontStyle = "default",
  lineClamp = 0,
  dangerouslySetInnerHTML,
  onReadMoreClick,
  ...props
}) => {
  const { _t } = useTranslate();
  const textRef = useRef(null);
  const [isClamped, setIsClamped] = useState(false);
  const showReadMore = !dangerouslySetInnerHTML && onReadMoreClick && isClamped;

  const checkClamped = useCallback(() => {
    if (textRef.current && !!lineClamp) {
      const maxHeight = Math.ceil(lineClamp * parseFloat(getComputedStyle(textRef.current).lineHeight));
      
      setIsClamped(Math.ceil(textRef.current.scrollHeight) > maxHeight);
    }
  }, [lineClamp]);

  useEffect(() => {
    checkClamped();
  }, [checkClamped]);

  return (
    <>
      <StyledText
        ref={textRef}
        $size={size}
        $weight={weight}
        $color={color}
        $lineClamp={lineClamp}
        $fontStyle={fontStyle}
        dangerouslySetInnerHTML={dangerouslySetInnerHTML}
        {...props}
      >
        {dangerouslySetInnerHTML ? null : _t(children)}
      </StyledText>

      {(showReadMore || dangerouslySetInnerHTML) && !!lineClamp && (
        <StyledAnchor onClick={onReadMoreClick} underline="hover" $size={size}>
          <T>Read more</T>
        </StyledAnchor>
      )}
    </>
  );
};

const sizeStyles = {
  default: css`
    font-size: calc(${({ theme }) => theme.fontSizes.p} * 0.875);
    line-height: 1.44;
  `,
  tiny: css`
    font-size: calc(${({ theme }) => theme.fontSizes.p} * 0.625);
    line-height: 1.6;
  `,
  small: css`
    font-size: calc(${({ theme }) => theme.fontSizes.p} * 0.75);
    line-height: 1.65;
  `,
  large: css`
    font-size: ${({ theme }) => theme.fontSizes.p};
    line-height: 1.65;

    ${({ theme }) => theme.containerMedia.sm} {
      font-size: calc(${({ theme }) => theme.fontSizes.p} * 0.875);
    }
  `,
  xlarge: css`
    font-size: calc(${({ theme }) => theme.fontSizes.p} * 1.125);
    line-height: 1.5;

    ${({ theme }) => theme.containerMedia.sm} {
      font-size: ${({ theme }) => theme.fontSizes.p};
    }
  `,
};

const colorStyles = {
  default: css`
    color: rgba(${({ theme }) => theme.colors.textSecondary.rgba});
  `,
  dark: css`
    color: rgba(${({ theme }) => theme.colors.text.rgba});
  `,
  accent: css`
    color: rgba(${({ theme }) => theme.colors.accent.rgba});
  `,
  accentContrast: css`
    color: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
  `,
  warning: css`
    color: rgba(${({ theme }) => theme.colors.warning.rgba});
  `,
  error: css`
    color: rgba(${({ theme }) => theme.colors.error.rgba});
  `,
  success: css`
    color: rgba(${({ theme }) => theme.colors.success.rgba});
  `,
};

const weightStyles = {
  regular: css`
    font-weight: 400;
  `,
  medium: css`
    font-weight: 500;
  `,
  bold: css`
    font-weight: 600;
  `,
};

const fontStyles = {
  default: css`
    font-style: normal;
  `,
  italic: css`
    font-style: italic;
  `,
};

export { sizeStyles as textSizeStyles };
export default Text;
